/**
 * Auth API types and parsers — mirrors Flutter `AuthResponseDto`, `MeSessionSnapshot`,
 * and request DTOs from `backend_sdk` / `me_session_service.dart`.
 *
 * Parsers accept camelCase and PascalCase JSON keys from the .NET API.
 */

/** Header value for workforce login + refresh (matches Flutter `clientAppProduct: 'workforce'`). */
export const WORKFORCE_APP_PRODUCT = 'workforce'

export const AUTH_APP_PRODUCT_HEADER = 'X-App-Product'

/** Relative paths (no `/api/v1` prefix) where Bearer must not be sent. */
export const AUTH_OMIT_BEARER_RELATIVE_PATHS = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/refresh-token',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/verify-reset-otp',
  '/auth/reset-password',
  '/auth/staff-invite/validate',
  '/auth/staff-invite/accept',
])

export type DeviceInfoRequest = {
  deviceName: string
  platform: string
  deviceFingerprint: string
  osVersion?: string | null
  deviceModel?: string | null
  auditContextJson?: string | null
}

export type LoginRequest = {
  email: string
  password: string
  deviceInfo: DeviceInfoRequest
}

export type RefreshTokenRequest = {
  refreshToken: string
  sessionId?: string | null
}

export type LogoutRequest = {
  sessionId: string
  refreshToken: string
}

export type AuthUser = {
  id: string
  email: string
  userType: string
  /** Platform staffing roles when the API includes them (desk routing). */
  roles: string[]
  capabilities: string[]
  sessionVersion: number
  isApplicantSealed: boolean
}

export type AuthResponse = {
  token: string
  refreshToken: string
  sessionId: string
  deviceId: string
  user: AuthUser
}

export type MeSessionNavOverride = {
  itemId: string
  hidden: boolean
  order: number | null
}

export type MeSessionSnapshot = {
  catalogVersion: number
  sessionVersion: number
  tenantId: string
  tenantName: string | null
  displayName: string
  roles: string[]
  capabilities: string[]
  navOverrides: MeSessionNavOverride[] | null
}

export function readStr(
  m: Record<string, unknown>,
  camel: string,
  pascal: string,
): string {
  const v = m[camel] ?? m[pascal]
  if (v == null) return ''
  return String(v).trim()
}

export function readInt(
  m: Record<string, unknown>,
  camel: string,
  pascal: string,
): number {
  const v = m[camel] ?? m[pascal]
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v)
  if (typeof v === 'string' && v.trim()) return Number.parseInt(v, 10) || 0
  return 0
}

function readBool(
  m: Record<string, unknown>,
  camel: string,
  pascal: string,
): boolean {
  const v = m[camel] ?? m[pascal]
  if (typeof v === 'boolean') return v
  if (v === 'true' || v === 'True') return true
  if (v === 'false' || v === 'False') return false
  return false
}

function readStringList(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v
    .map((e) => (e == null ? '' : String(e).trim()))
    .filter((s) => s.length > 0)
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object') return null
  return v as Record<string, unknown>
}

export function parseAuthUser(data: unknown): AuthUser {
  const m = asRecord(data)
  if (!m) {
    return {
      id: '',
      email: '',
      userType: '',
      roles: [],
      capabilities: [],
      sessionVersion: 0,
      isApplicantSealed: false,
    }
  }
  return {
    id: readStr(m, 'id', 'Id'),
    email: readStr(m, 'email', 'Email'),
    userType: readStr(m, 'userType', 'UserType'),
    roles: readStringList(m.roles ?? m.Roles),
    capabilities: readStringList(m.capabilities ?? m.Capabilities),
    sessionVersion: readInt(m, 'sessionVersion', 'SessionVersion'),
    isApplicantSealed: readBool(m, 'isApplicantSealed', 'IsApplicantSealed'),
  }
}

export function parseAuthResponse(data: unknown): AuthResponse {
  if (!data || typeof data !== 'object') {
    throw new Error('Empty or invalid auth response from server.')
  }
  const m = data as Record<string, unknown>
  const userRaw = m.user ?? m.User
  const user = parseAuthUser(userRaw)
  if (!user.id && !user.email) {
    throw new Error('Auth response missing user.')
  }
  return {
    token: readStr(m, 'token', 'Token'),
    refreshToken: readStr(m, 'refreshToken', 'RefreshToken'),
    sessionId: readStr(m, 'sessionId', 'SessionId'),
    deviceId: readStr(m, 'deviceId', 'DeviceId'),
    user,
  }
}

export function parseMeSessionNavOverride(data: unknown): MeSessionNavOverride | null {
  const m = asRecord(data)
  if (!m) return null
  const itemId = readStr(m, 'itemId', 'ItemId')
  if (!itemId) return null
  const orderRaw = m.order ?? m.Order
  let order: number | null = null
  if (typeof orderRaw === 'number' && Number.isFinite(orderRaw)) {
    order = Math.trunc(orderRaw)
  } else if (typeof orderRaw === 'string' && orderRaw.trim()) {
    order = Number.parseInt(orderRaw, 10)
    if (Number.isNaN(order)) order = null
  }
  return {
    itemId,
    hidden: readBool(m, 'hidden', 'Hidden'),
    order,
  }
}

/** Mirrors Flutter `MeSessionSnapshot.fromJson`. */
export function parseMeSessionSnapshot(data: unknown): MeSessionSnapshot {
  if (!data || typeof data !== 'object') {
    throw new Error('Empty response from /auth/me/session')
  }
  const m = data as Record<string, unknown>
  const user = asRecord(m.user ?? m.User) ?? {}

  const navRaw = m.navOverrides ?? m.NavOverrides
  let navOverrides: MeSessionNavOverride[] | null = null
  if (Array.isArray(navRaw)) {
    const parsed = navRaw
      .map((e) => parseMeSessionNavOverride(e))
      .filter((e): e is MeSessionNavOverride => e != null)
    navOverrides = parsed.length > 0 ? parsed : null
  }

  const tenantNameRaw = m.tenantName ?? m.TenantName
  const tenantName =
    tenantNameRaw == null ? null : String(tenantNameRaw).trim() || null

  return {
    catalogVersion: readInt(m, 'catalogVersion', 'CatalogVersion'),
    sessionVersion: readInt(m, 'sessionVersion', 'SessionVersion'),
    tenantId: readStr(m, 'tenantId', 'TenantId'),
    tenantName,
    displayName: readStr(user, 'displayName', 'DisplayName'),
    roles: readStringList(user.roles ?? user.Roles),
    capabilities: readStringList(user.capabilities ?? user.Capabilities),
    navOverrides,
  }
}

export type StaffInviteValidation = {
  valid: boolean
  email: string | null
  displayName: string | null
  roleLabel: string | null
  requiresPassword: boolean
}

export function parseStaffInviteValidation(data: unknown): StaffInviteValidation {
  const m = asRecord(data)
  if (!m) {
    return {
      valid: false,
      email: null,
      displayName: null,
      roleLabel: null,
      requiresPassword: false,
    }
  }
  const email = readStr(m, 'email', 'Email')
  const displayName = readStr(m, 'displayName', 'DisplayName')
  const roleLabel = readStr(m, 'roleLabel', 'RoleLabel')
  return {
    valid: readBool(m, 'valid', 'Valid'),
    email: email || null,
    displayName: displayName || null,
    roleLabel: roleLabel || null,
    requiresPassword: readBool(m, 'requiresPassword', 'RequiresPassword'),
  }
}

export function buildDeviceInfoRequestBody(
  deviceInfo: DeviceInfoRequest,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    deviceName: deviceInfo.deviceName,
    platform: deviceInfo.platform,
    deviceFingerprint: deviceInfo.deviceFingerprint,
  }
  if (deviceInfo.osVersion != null && deviceInfo.osVersion !== '') {
    body.osVersion = deviceInfo.osVersion
  }
  if (deviceInfo.deviceModel != null && deviceInfo.deviceModel !== '') {
    body.deviceModel = deviceInfo.deviceModel
  }
  if (
    deviceInfo.auditContextJson != null &&
    deviceInfo.auditContextJson !== ''
  ) {
    body.auditContextJson = deviceInfo.auditContextJson
  }
  return body
}

export function buildLoginRequestBody(request: LoginRequest): Record<string, unknown> {
  return {
    email: request.email.trim(),
    password: request.password,
    deviceInfo: buildDeviceInfoRequestBody(request.deviceInfo),
  }
}

export function buildRefreshTokenRequestBody(
  request: RefreshTokenRequest,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    refreshToken: request.refreshToken,
  }
  const sessionId = request.sessionId?.trim()
  if (sessionId) {
    body.sessionId = sessionId
  }
  return body
}

export function buildLogoutRequestBody(request: LogoutRequest): Record<string, unknown> {
  return {
    sessionId: request.sessionId,
    refreshToken: request.refreshToken,
  }
}

/**
 * Workspace web device info — aligned with Flutter web `DeviceInfoService` defaults.
 * Pass a stable fingerprint (persist in localStorage when wiring auth UI).
 */
export function buildWorkspaceWebDeviceInfo(
  deviceFingerprint: string,
  options?: {
    userAgent?: string
    browserName?: string
    platform?: string
  },
): DeviceInfoRequest {
  const ua =
    options?.userAgent ??
    (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  const auditContextJson = JSON.stringify({
    schemaVersion: 1,
    collectedAtUtc: new Date().toISOString(),
    platform: 'web',
    web: {
      browserName: options?.browserName ?? 'Browser',
      userAgent: ua,
      platform: options?.platform ?? 'Web',
    },
  })
  return {
    deviceFingerprint,
    deviceName: options?.browserName ?? 'Browser',
    platform: 'Web',
    osVersion: ua.slice(0, 100) || null,
    deviceModel: options?.platform ?? 'Web',
    auditContextJson,
  }
}

/** Normalize axios relative URL to a path comparable to [AUTH_OMIT_BEARER_RELATIVE_PATHS]. */
export function normalizeAuthRelativePath(url: string | undefined): string {
  if (!url) return ''
  let path = url.split('?')[0] ?? url
  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      path = new URL(path).pathname
    } catch {
      // keep path as-is
    }
  }
  if (path.includes('/api/v1/')) {
    path = path.slice(path.indexOf('/api/v1/') + '/api/v1'.length)
  }
  if (!path.startsWith('/')) path = `/${path}`
  return path
}
